import React, { useEffect, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Alert, Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import PaidIcon from "@mui/icons-material/Paid";
import { PayrollService } from "services/payroll";

const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const STATUS_COLORS = { DRAFT: "default", CALCULATED: "info", APPROVED: "success", PAID: "primary" };
const fmtCLP = (n) => "$" + Math.round(n || 0).toLocaleString("es-CL");

function RunDetailDialog({ open, onClose, runId, onAction }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !runId) return;
    setLoading(true);
    setError(null);
    PayrollService.getRun(runId)
      .then(setData)
      .catch((e) => setError(e?.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [open, runId]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Detalle de liquidación</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        {loading ? (
          <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress /></Box>
        ) : !data ? null : (
          <Box>
            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
              <Paper sx={{ p: 2, flex: 1 }}>
                <Typography variant="caption" color="text.secondary">Período</Typography>
                <Typography variant="h6">{MONTHS[data.run.period.month - 1]} {data.run.period.year}</Typography>
              </Paper>
              <Paper sx={{ p: 2, flex: 1 }}>
                <Typography variant="caption" color="text.secondary">Imponible</Typography>
                <Typography variant="h6">{fmtCLP(data.run.totalImponible)}</Typography>
              </Paper>
              <Paper sx={{ p: 2, flex: 1 }}>
                <Typography variant="caption" color="text.secondary">Líquido total</Typography>
                <Typography variant="h6" color="success.main">{fmtCLP(data.run.totalNetPay)}</Typography>
              </Paper>
              <Paper sx={{ p: 2, flex: 1 }}>
                <Typography variant="caption" color="text.secondary">Costo empleador</Typography>
                <Typography variant="h6" color="error.main">{fmtCLP(data.run.totalEmployerCosts)}</Typography>
              </Paper>
            </Box>

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>RUT</TableCell>
                    <TableCell>Empleado</TableCell>
                    <TableCell align="right">Imponible</TableCell>
                    <TableCell align="right">AFP</TableCell>
                    <TableCell align="right">Salud</TableCell>
                    <TableCell align="right">Cesantía</TableCell>
                    <TableCell align="right">Impto.</TableCell>
                    <TableCell align="right">Líquido</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.results.map((r) => (
                    <TableRow key={r._id}>
                      <TableCell><code>{r.employee?.rut}</code></TableCell>
                      <TableCell>{r.employee?.firstName} {r.employee?.lastName}</TableCell>
                      <TableCell align="right">{fmtCLP(r.result.earnings.totalImponible)}</TableCell>
                      <TableCell align="right">{fmtCLP(r.result.deductions.afp)}</TableCell>
                      <TableCell align="right">{fmtCLP(r.result.deductions.health)}</TableCell>
                      <TableCell align="right">{fmtCLP(r.result.deductions.unemployment)}</TableCell>
                      <TableCell align="right">{fmtCLP(r.result.deductions.incomeTax)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtCLP(r.result.netPay)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {data?.run?.status === "CALCULATED" && (
          <Button variant="contained" color="success" startIcon={<CheckIcon />} onClick={() => onAction("approve", data.run)}>
            Aprobar
          </Button>
        )}
        {(data?.run?.status === "CALCULATED" || data?.run?.status === "APPROVED" || data?.run?.status === "PAID") && (
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => onAction("previred", data.run)}>
            Previred
          </Button>
        )}
        {data?.run?.status === "APPROVED" && (
          <Button variant="outlined" color="primary" startIcon={<PaidIcon />} onClick={() => onAction("paid", data.run)}>
            Marcar pagada
          </Button>
        )}
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

export function RunsPanel({ memberId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const now = new Date();
  const [period, setPeriod] = useState({ month: now.getMonth() + 1, year: now.getFullYear() });
  const [creating, setCreating] = useState(false);
  const [detailRun, setDetailRun] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await PayrollService.listRuns(memberId);
      setItems(res.items || []);
    } catch (e) {
      setFeedback({ kind: "error", msg: e?.response?.data?.error || e.message });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [memberId]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await PayrollService.runCalculate(memberId, period);
      setFeedback({ kind: "ok", msg: `Corrida calculada: ${res.run.employeeCount} empleados, líquido total ${fmtCLP(res.run.totalNetPay)}` });
      setCreateOpen(false);
      await load();
    } catch (e) {
      setFeedback({ kind: "error", msg: e?.response?.data?.error || e.message });
    } finally { setCreating(false); }
  };

  const handleAction = async (action, run) => {
    try {
      if (action === "approve") {
        await PayrollService.approveRun(run._id);
        setFeedback({ kind: "ok", msg: "Aprobada — asiento contable generado" });
      } else if (action === "previred") {
        await PayrollService.downloadPrevired(run._id, run.period);
        setFeedback({ kind: "ok", msg: "Previred descargado" });
      } else if (action === "paid") {
        await PayrollService.markRunPaid(run._id);
        setFeedback({ kind: "ok", msg: "Marcada como pagada" });
      }
      setDetailRun(null);
      await load();
    } catch (e) {
      setFeedback({ kind: "error", msg: e?.response?.data?.error || e.message });
    }
  };

  return (
    <Box>
      {feedback && <Alert severity={feedback.kind === "ok" ? "success" : "error"} onClose={() => setFeedback(null)} sx={{ mb: 2 }}>{feedback.msg}</Alert>}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={() => setCreateOpen(true)}>
          Calcular liquidación del mes
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Período</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Empleados</TableCell>
                <TableCell align="right">Imponible</TableCell>
                <TableCell align="right">Líquido</TableCell>
                <TableCell align="right">Costo emp.</TableCell>
                <TableCell>Aprobada</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3 }}><CircularProgress size={20} /></TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3, color: "text.secondary" }}>Sin liquidaciones</TableCell></TableRow>
              ) : items.map((r) => (
                <TableRow key={r._id} hover>
                  <TableCell>{MONTHS[r.period.month - 1]} {r.period.year}</TableCell>
                  <TableCell><Chip size="small" label={r.status} color={STATUS_COLORS[r.status] || "default"} /></TableCell>
                  <TableCell align="right">{r.employeeCount}</TableCell>
                  <TableCell align="right">{fmtCLP(r.totalImponible)}</TableCell>
                  <TableCell align="right">{fmtCLP(r.totalNetPay)}</TableCell>
                  <TableCell align="right">{fmtCLP(r.totalEmployerCosts)}</TableCell>
                  <TableCell>{r.approvedAt ? new Date(r.approvedAt).toLocaleDateString("es-CL") : "—"}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => setDetailRun(r)}><VisibilityIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Modal crear */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)}>
        <DialogTitle>Calcular liquidación</DialogTitle>
        <DialogContent sx={{ display: "flex", gap: 2, pt: 2 }}>
          <Select size="small" value={period.month} onChange={(e) => setPeriod({ ...period, month: e.target.value })}>
            {MONTHS.map((m, i) => <MenuItem key={m} value={i + 1}>{m}</MenuItem>)}
          </Select>
          <Select size="small" value={period.year} onChange={(e) => setPeriod({ ...period, year: e.target.value })}>
            {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? <CircularProgress size={20} /> : "Calcular"}
          </Button>
        </DialogActions>
      </Dialog>

      <RunDetailDialog open={!!detailRun} onClose={() => setDetailRun(null)} runId={detailRun?._id} onAction={handleAction} />
    </Box>
  );
}
